import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

const DeviceTrafficChart = ({ data, interval }) => {
  // Function to format device type names
  const formatDeviceType = (deviceType) => {
    if (!deviceType) return "Unknown";
    
    // Handle different device type formats
    const formatted = deviceType
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/Mobile\s+/i, "Mobile ") // Normalize "Mobile" spacing
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
    
    return formatted;
  };

  // Function to format device traffic data for chart
  const formatDeviceDataForChart = (data) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data
      .map((item) => {
        // item format: [device_type, visit_count, unique_visitors, percentage, avg_page_load_ms]
        const rawDeviceType = item[0] || "Unknown";
        const visitCount = item[1] || 0;
        const uniqueVisitors = item[2] || 0;
        const percentage = item[3] ? parseFloat(item[3]) : 0;
        const avgPageLoadMs = item[4] ? parseFloat(item[4]) : 0;

        return {
          deviceType: formatDeviceType(rawDeviceType),
          rawDeviceType: rawDeviceType,
          visitCount: visitCount,
          uniqueVisitors: uniqueVisitors,
          percentage: percentage,
          avgPageLoadMs: avgPageLoadMs,
        };
      })
      .filter((item) => item.visitCount > 0); // Only show devices with actual visits
  };

  const chartData = formatDeviceDataForChart(data);

  if (!chartData || chartData.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          color: "#a0aec0",
          fontSize: "14px",
        }}
      >
        📊 No device data available
        <small style={{ fontSize: "11px", color: "#cbd5e0", marginTop: "8px" }}>
          Try selecting a different date range
        </small>
      </div>
    );
  }

  // Color palette for different device types
  const getDeviceColor = (deviceType) => {
    const deviceTypeLower = deviceType.toLowerCase();
    
    if (deviceTypeLower.includes("desktop")) {
      return "#48bb78"; // Green
    } else if (deviceTypeLower.includes("mobile") && deviceTypeLower.includes("ios")) {
      return "#667eea"; // Purple
    } else if (deviceTypeLower.includes("mobile") && deviceTypeLower.includes("android")) {
      return "#3B82F6"; // Blue
    } else if (deviceTypeLower.includes("mobile")) {
      return "#667eea"; // Purple for generic mobile
    } else if (deviceTypeLower.includes("tablet")) {
      return "#f59e0b"; // Orange
    } else {
      return "#94a3b8"; // Gray for unknown
    }
  };

  // Custom tooltip to show all metrics
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            padding: "12px",
            fontSize: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          }}
        >
          <p
            style={{
              margin: "0 0 8px 0",
              fontWeight: "700",
              color: "#2d3748",
              borderBottom: "1px solid #e2e8f0",
              paddingBottom: "6px",
            }}
          >
            {dataPoint.deviceType}
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              margin: "4px 0",
              color: "#667eea",
            }}
          >
            <span>Visit Count:</span>
            <span style={{ fontWeight: "700" }}>
              {dataPoint.visitCount.toLocaleString()}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              margin: "4px 0",
              color: "#3B82F6",
            }}
          >
            <span>Unique Visitors:</span>
            <span style={{ fontWeight: "700" }}>
              {dataPoint.uniqueVisitors.toLocaleString()}
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              margin: "4px 0",
              color: "#10B981",
            }}
          >
            <span>Percentage:</span>
            <span style={{ fontWeight: "700" }}>
              {dataPoint.percentage.toFixed(2)}%
            </span>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              margin: "4px 0",
              color: "#F59E0B",
              borderTop: "1px solid #e2e8f0",
              paddingTop: "6px",
              marginTop: "6px",
            }}
          >
            <span>Avg Load Time:</span>
            <span style={{ fontWeight: "700" }}>
              {dataPoint.avgPageLoadMs.toFixed(2)}ms
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate the minimum Y-axis value to ensure small bars are visible
  const maxValue = Math.max(...chartData.map(item => item.visitCount));
  const yAxisDomain = [0, Math.ceil(maxValue * 1.1)]; // Add 10% padding at top

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={chartData}
        margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis
          dataKey="deviceType"
          stroke="#718096"
          style={{ fontSize: "12px" }}
          angle={-20}
          textAnchor="end"
          height={60}
          interval={0}
        />
        <YAxis
          stroke="#718096"
          style={{ fontSize: "12px" }}
          allowDecimals={false}
          domain={yAxisDomain}
          label={{
            value: "Visit Count",
            angle: -90,
            position: "insideLeft",
            style: { fontSize: "12px", fill: "#718096" },
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          wrapperStyle={{ fontSize: "12px" }}
          iconType="rect"
        />

        {/* Visit Count Bars with Dynamic Colors */}
        <Bar 
          dataKey="visitCount" 
          name="Visit Count" 
          radius={[8, 8, 0, 0]}
          minPointSize={5}
        >
          {chartData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={getDeviceColor(entry.rawDeviceType)} 
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

export default DeviceTrafficChart;